// HELPDOCS1 (docs/build-briefs/HELPDOCS1-guides-and-screen-help.md; design/style-guide.md sections 3, 11, 13, 14): the Help panel
// of the location and franchisor consoles. A docked panel on the right (never a modal, design-language "Dialogs and recovery":
// Help never uses a modal), opened by the header's Help button, that shows the guide section for the screen the person is on
// and lets them browse every guide. The console hands in how to read its own guides (its own session, its own base path) and
// how to name the screen it is on; nothing here reaches another console's session.
//
// Every shown word is set with textContent. Guide text is drawn by helpBlocks.ts. The panel is built at runtime with its own
// classes (check:chrome-ratchet), keeps the open screen and any draft untouched underneath, closes on Escape or Close, and
// returns focus to the Help button.

import { renderGuideBlocks, sectionDomId } from "./helpBlocks.ts";
import { groupGuidesByRhythm, knownRoleId, type HelpRole } from "./helpRoles.ts";
import {
  HELP_GUIDE_FAILED,
  HELP_LIST_FAILED,
  isGuideReply,
  parseGuide,
  type HelpGuideSummaryDto,
} from "./ownerHelpModel.ts";

/** The guide section a screen opens. `section` absent opens the guide from its top. */
export interface HelpTarget {
  readonly slug: string;
  readonly section?: string;
}

export interface HelpGuideListEntry extends HelpGuideSummaryDto {
  group: string;
  roles?: string[];
  sections: { id: string; title: string }[];
}

export interface HelpPanelHost {
  /** A JSON GET under the console's help base path: "" is the list, "/<slug>" one guide. A 401 has already ended the session. */
  request<T>(suffix: string): Promise<{ status: number; body: T | null }>;
  /** An image GET under the same base path, with the session's token, as a blob; null on any failure. */
  requestImage(name: string): Promise<Blob | null>;
  /** The guide section for the screen on show right now. Read on every open and every "This screen". */
  target(): HelpTarget;
  /** The console's own support request sheet ("Get help"), when it has one. */
  contactSupport?: () => void;
  /** False for the franchisor console: its guides are in the app only and the footer carries no link to the public site (TEN15). */
  publicSiteLink?: boolean;
  /** The roles the All guides view offers. Absent: one role, so the list is grouped by rhythm with no picker. */
  roles?: readonly HelpRole[];
  /** Where the picked role is remembered in this browser (local storage, never required). */
  roleStorageKey?: string;
}

export interface HelpPanel {
  /** Opens the panel on the guide section for the current screen. Open already: shows that section again. */
  openForScreen(): void;
  /** Opens one guide section by name (a guide's own link, a test). */
  openTarget(target: HelpTarget): void;
  close(): void;
  isOpen(): boolean;
  /** Signed out: closes the panel and forgets everything read. */
  reset(): void;
}

export const HELP_SITE_URL = "https://snipsip.com/help/";

function make<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className !== undefined) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function isListReply(body: unknown): body is { guides: HelpGuideListEntry[] } {
  if (typeof body !== "object" || body === null) return false;
  const guides = (body as { guides?: unknown }).guides;
  return (
    Array.isArray(guides) &&
    guides.every(
      (g) =>
        typeof g === "object" &&
        g !== null &&
        typeof (g as HelpGuideListEntry).slug === "string" &&
        typeof (g as HelpGuideListEntry).title === "string" &&
        typeof (g as HelpGuideListEntry).summary === "string" &&
        typeof (g as HelpGuideListEntry).group === "string",
    )
  );
}

export function createHelpPanel(trigger: HTMLElement, host: HelpPanelHost): HelpPanel {
  let panel: HTMLElement | null = null;
  let titleEl: HTMLElement;
  let bodyEl: HTMLElement;
  let allButton: HTMLButtonElement;
  let guides: HelpGuideListEntry[] | null = null;
  const loaded = new Map<string, string>();
  let blobUrls: string[] = [];
  // Bumped on every draw and on reset, so a reply that arrives after the person moved on is dropped.
  let generation = 0;
  // True while the panel follows the screen: moving to another screen (the address changes) re-opens the matching section.
  // False once the person browses by hand, until they tap "This screen".
  let followsScreen = true;
  let shown: { slug: string; section?: string } | null = null;
  let pickedRole: string | undefined = readRememberedRole();

  /** The page makes room for the panel instead of sitting under it (design language rule 5): styles.css reads this mark. */
  function markPageOpen(open: boolean): void {
    document.body.toggleAttribute("data-help-open", open);
  }

  function readRememberedRole(): string | undefined {
    if (host.roles === undefined || host.roleStorageKey === undefined) return undefined;
    try {
      return knownRoleId(host.roles, window.localStorage.getItem(host.roleStorageKey));
    } catch {
      return undefined;
    }
  }

  function rememberRole(): void {
    if (host.roleStorageKey === undefined) return;
    try {
      if (pickedRole === undefined) window.localStorage.removeItem(host.roleStorageKey);
      else window.localStorage.setItem(host.roleStorageKey, pickedRole);
    } catch {
      // Remembering is a convenience: a browser that refuses storage just forgets.
    }
  }

  function releaseImages(): void {
    for (const url of blobUrls) URL.revokeObjectURL(url);
    blobUrls = [];
  }

  function build(): HTMLElement {
    const aside = make("aside", "help-panel");
    aside.dataset["testid"] = "help-panel";
    aside.setAttribute("role", "complementary");
    aside.setAttribute("aria-labelledby", "help-panel-title");
    aside.hidden = true;

    const head = make("header", "help-panel-head");
    titleEl = make("h2", "help-panel-title", "Help");
    titleEl.id = "help-panel-title";
    titleEl.tabIndex = -1;
    const close = make("button", "btn-secondary btn-compact", "Close");
    close.type = "button";
    close.dataset["testid"] = "help-panel-close";
    close.addEventListener("click", () => api.close());
    head.append(titleEl, close);

    const tools = make("div", "help-panel-tools");
    allButton = make("button", "btn-secondary btn-compact", "All guides");
    allButton.type = "button";
    allButton.dataset["testid"] = "help-panel-all";
    allButton.addEventListener("click", () => {
      followsScreen = false;
      void drawList();
    });
    const hereButton = make("button", "btn-secondary btn-compact", "This screen");
    hereButton.type = "button";
    hereButton.dataset["testid"] = "help-panel-here";
    hereButton.addEventListener("click", () => api.openForScreen());
    // Narrow screens show the panel full width (styles.css); this is the clear way back to the screen under it.
    const backButton = make("button", "btn-secondary btn-compact help-panel-back", "Back to screen");
    backButton.type = "button";
    backButton.dataset["testid"] = "help-panel-back";
    backButton.addEventListener("click", () => api.close());
    tools.append(allButton, hereButton, backButton);

    bodyEl = make("div", "help-panel-body");
    bodyEl.dataset["testid"] = "help-panel-body";

    const foot = make("footer", "help-panel-foot");
    if (host.publicSiteLink !== false) {
      const site = make("a", "help-panel-link", "Help site");
      site.href = HELP_SITE_URL;
      site.target = "_blank";
      site.rel = "noopener noreferrer";
      site.dataset["testid"] = "help-panel-site";
      foot.append(site);
    }
    if (host.contactSupport !== undefined) {
      const contact = make("button", "btn-secondary btn-compact", "Contact support");
      contact.type = "button";
      contact.dataset["testid"] = "help-panel-contact";
      contact.addEventListener("click", () => host.contactSupport?.());
      foot.append(contact);
    }

    aside.append(head, tools, bodyEl, foot);
    aside.addEventListener("keydown", (event) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        api.close();
      }
    });
    document.body.append(aside);
    return aside;
  }

  function showFailure(message: string, retry: () => void): void {
    const wrap = make("div", "help-panel-state");
    wrap.dataset["testid"] = "help-panel-error";
    wrap.append(make("p", "help-panel-error", message));
    const again = make("button", "btn-secondary btn-compact", "Try again");
    again.type = "button";
    again.addEventListener("click", retry);
    wrap.append(again);
    bodyEl.replaceChildren(wrap);
  }

  async function ensureList(mine: number): Promise<boolean> {
    if (guides !== null) return true;
    const reply = await host.request<unknown>("");
    if (mine !== generation) return false;
    if (reply.status === 200 && isListReply(reply.body)) {
      guides = reply.body.guides;
      return true;
    }
    showFailure(HELP_LIST_FAILED, () => void drawList());
    return false;
  }

  async function drawList(): Promise<void> {
    const mine = ++generation;
    releaseImages();
    shown = null;
    titleEl.textContent = "Help";
    allButton.hidden = true;
    bodyEl.replaceChildren(make("p", "help-panel-state", "Loading the guides..."));
    if (!(await ensureList(mine)) || guides === null) return;
    const frag = document.createDocumentFragment();
    if (host.roles !== undefined) {
      frag.append(make("h3", "help-panel-group", "Show guides for"));
      const picker = make("div", "help-role-picker");
      picker.setAttribute("role", "group");
      picker.setAttribute("aria-label", "Show guides for a role");
      for (const role of host.roles) {
        const chip = make("button", "help-role-chip", role.label);
        chip.type = "button";
        chip.dataset["testid"] = "help-role";
        chip.dataset["role"] = role.id;
        chip.setAttribute("aria-pressed", pickedRole === role.id ? "true" : "false");
        chip.addEventListener("click", () => {
          pickedRole = pickedRole === role.id ? undefined : role.id;
          rememberRole();
          void drawList();
        });
        picker.append(chip);
      }
      frag.append(picker);
    }
    const roleLabel = host.roles?.find((role) => role.id === pickedRole)?.label;
    if (roleLabel !== undefined) {
      frag.append(make("p", "help-panel-state", `Showing the guides for ${roleLabel}. Tap ${roleLabel} again to see them all.`));
    }
    for (const [group, list] of groupGuidesByRhythm(guides, pickedRole)) {
      frag.append(make("h3", "help-panel-group", group));
      const ul = make("ul", "so-help-list");
      for (const guide of list) {
        const li = make("li");
        const button = make("button", "so-help-item");
        button.type = "button";
        button.dataset["testid"] = "help-panel-guide";
        button.dataset["slug"] = guide.slug;
        button.append(
          make("span", "so-help-item-title", guide.title),
          make("span", "so-help-item-summary", guide.summary),
        );
        button.addEventListener("click", () => {
          followsScreen = false;
          void drawGuide({ slug: guide.slug });
        });
        li.append(button);
        ul.append(li);
      }
      frag.append(ul);
    }
    bodyEl.replaceChildren(frag);
    bodyEl.scrollTop = 0;
    titleEl.focus();
  }

  async function drawGuide(target: HelpTarget): Promise<void> {
    const mine = ++generation;
    releaseImages();
    shown = target.section === undefined ? { slug: target.slug } : { slug: target.slug, section: target.section };
    allButton.hidden = false;
    bodyEl.replaceChildren(make("p", "help-panel-state", "Loading the guide..."));
    let markdown = loaded.get(target.slug);
    let title = "";
    let summary = "";
    if (markdown === undefined) {
      const reply = await host.request<unknown>(`/${encodeURIComponent(target.slug)}`);
      if (mine !== generation) return;
      if (reply.status === 404) {
        // A screen mapped to a guide that is not there falls back to the guide home, never an empty panel.
        await drawList();
        return;
      }
      if (reply.status !== 200 || !isGuideReply(reply.body)) {
        showFailure(HELP_GUIDE_FAILED, () => void drawGuide(target));
        return;
      }
      markdown = reply.body.guide.markdown;
      title = reply.body.guide.title;
      summary = reply.body.guide.summary;
      loaded.set(target.slug, markdown);
    }
    if (title === "") {
      const meta = guides?.find((g) => g.slug === target.slug);
      title = meta?.title ?? markdown.split("\n")[0]?.replace(/^#\s*/, "") ?? "Help";
      summary = meta?.summary ?? "";
    }
    titleEl.textContent = title;
    const article = make("article", "so-help-article help-panel-article");
    article.dataset["testid"] = "help-panel-article";
    if (summary !== "") article.append(make("p", "so-help-guide-summary", summary));
    bodyEl.replaceChildren(article);
    await renderGuideBlocks(article, parseGuide(markdown), {
      loadImage: (name) => host.requestImage(name),
      isCurrent: () => mine === generation,
      keepUrl: (url) => blobUrls.push(url),
      openGuide: (slug, section) => {
        followsScreen = false;
        void drawGuide(section === undefined ? { slug } : { slug, section });
      },
    });
    if (mine !== generation) return;
    const heading =
      target.section === undefined ? null : article.querySelector<HTMLElement>(`#${sectionDomId(target.section)}`);
    if (heading === null) {
      bodyEl.scrollTop = 0;
      titleEl.focus();
    } else {
      // Images above the section load first and shift it: scroll after they have all been tried.
      heading.scrollIntoView({ block: "start" });
      heading.focus({ preventScroll: true });
    }
  }

  function onAddressChange(): void {
    if (panel === null || panel.hidden || !followsScreen) return;
    const next = host.target();
    if (shown !== null && shown.slug === next.slug && shown.section === next.section) return;
    void drawGuide(next);
  }
  window.addEventListener("hashchange", onAddressChange);

  const api: HelpPanel = {
    openForScreen() {
      if (panel === null) panel = build();
      followsScreen = true;
      panel.hidden = false;
      markPageOpen(true);
      trigger.setAttribute("aria-expanded", "true");
      // The list is read once, for the "All guides" view and the group headings; a target needs only its own guide.
      void drawGuide(host.target());
    },
    openTarget(target) {
      if (panel === null) panel = build();
      followsScreen = false;
      panel.hidden = false;
      markPageOpen(true);
      trigger.setAttribute("aria-expanded", "true");
      void drawGuide(target);
    },
    close() {
      if (panel === null || panel.hidden) return;
      panel.hidden = true;
      markPageOpen(false);
      generation++;
      releaseImages();
      trigger.setAttribute("aria-expanded", "false");
      trigger.focus();
    },
    isOpen() {
      return panel !== null && !panel.hidden;
    },
    reset() {
      generation++;
      releaseImages();
      guides = null;
      loaded.clear();
      shown = null;
      markPageOpen(false);
      if (panel !== null) {
        panel.remove();
        panel = null;
      }
      trigger.setAttribute("aria-expanded", "false");
    },
  };
  return api;
}
